import { Card } from "antd";
import { X_AXIS_KEY } from "components/Graph/constants";
import { useHiddenLinesLocalStorage } from "components/Graph/hooks/useHiddenLinesLocalStorage";
import { useHighlights } from "components/Graph/hooks/useHighlights";
import { formatKeyValue } from "components/Graph/hooks/useKeyFormatter";
import { GraphId } from "components/Graph/types";
import { Highlights } from "components/Graph/UI/Highlights/Highlights";
import { GraphStateRenderer } from "components/Graph/UI/StateRenderers/GraphStateRenderer";
import { GeneralGraphUtils } from "components/Graph/utils/GeneralGraphUtils";
import { TimeGraphUtils } from "components/Graph/utils/TimeGraphUtils";
import dayjs from "dayjs";
import { useCurrencies } from "hooks/useCurrencies";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { DATE_FORMAT, DATE_FORMAT_WITH_TIME } from "localConstants";
import { useState } from "react";
import { Area, CartesianGrid, ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PermissionEnum } from "types/enums";

interface Props<TData extends object, CPath extends keyof TData> {
    data: TData[] | undefined;
    keys: CPath[];
    defaultVisibleKeys: CPath[];
    defaultActiveKeys: CPath[];
    graphId: GraphId;
    loading?: boolean;
    error?: boolean;
    summaryData?: object;
}

export const TimeGraph = <TData extends object, CPath extends keyof TData>({
    keys,
    defaultVisibleKeys,
    defaultActiveKeys,
    graphId,
    loading,
    error,
    summaryData,
    ...rest
}: Props<TData, CPath>) => {
    const { get } = useCurrencies();
    const { t } = usePaldockTranslation("ui.report.columns");

    const canViewGraph = usePermission().hasPermission(PermissionEnum.ReportShowInGraph);

    const data = rest.data || [];
    const utils = new TimeGraphUtils(data);

    // Detect if data contains hourly entries (same day with different hours)
    const isHourlyData = (() => {
        if (data.length < 2) return false;
        const dates = data
            .map((item) => (item as Record<string, unknown>)[X_AXIS_KEY])
            .filter((d): d is string => typeof d === "string")
            .map((d) => dayjs(d).format("YYYY-MM-DD"));
        const uniqueDates = new Set(dates);
        return uniqueDates.size === 1 && data.length > 1;
    })();

    const xAxisDateFormat = isHourlyData ? "HH:mm" : DATE_FORMAT;
    const tooltipDateFormat = isHourlyData ? DATE_FORMAT_WITH_TIME : DATE_FORMAT;


    const { highlights, handleClick, handleHide, handleAdd, activeHighlights, colorKeyMap } = useHighlights({
        data,
        keys,
        graphId,
        defaultActiveKeys,
        defaultVisibleKeys,
    });


    const graphData = utils.getGraphData(activeHighlights);
    const highlightsData = summaryData ? [summaryData as TData] : utils.getHighlightsData(keys);

    const { primaryKeys, secondaryKeys, merge } = graphData;
    const isEmpty = !loading && !error && merge.length === 0;

    const { get: getHiddenLines } = useHiddenLinesLocalStorage();
    const [hiddenLines] = useState<Set<string>>(() => new Set(getHiddenLines(graphId)));

    // Calculate domains with proper alignment of zero points
    const calculateAlignedDomains = () => {
        if (merge.length === 0) return { primary: [0, 1], secondary: [0, 1], primaryTicks: [0, 1], secondaryTicks: [0, 1] };

        const visiblePrimaryKeys = primaryKeys.filter((key) => !hiddenLines.has(key));
        const visibleSecondaryKeys = secondaryKeys.filter((key) => !hiddenLines.has(key));

        // Get actual min/max for each axis
        let primaryMin = Infinity, primaryMax = -Infinity;
        let secondaryMin = Infinity, secondaryMax = -Infinity;

        merge.forEach((item) => {
            visiblePrimaryKeys.forEach((key) => {
                const value = Number(item[key]);
                if (!isNaN(value) && isFinite(value)) {
                    primaryMin = Math.min(primaryMin, value);
                    primaryMax = Math.max(primaryMax, value);
                }
            });
            visibleSecondaryKeys.forEach((key) => {
                const value = Number(item[key]);
                if (!isNaN(value) && isFinite(value)) {
                    secondaryMin = Math.min(secondaryMin, value);
                    secondaryMax = Math.max(secondaryMax, value);
                }
            });
        });

        // Handle empty data
        if (!isFinite(primaryMin)) { primaryMin = 0; primaryMax = 1; }
        if (!isFinite(secondaryMin)) { secondaryMin = 0; secondaryMax = 1; }

        // Track if actual data has negative values
        const primaryHasNegative = primaryMin < 0;
        const secondaryHasNegative = secondaryMin < 0;

        // Store original max values for step calculation (no padding)
        const primaryMaxOriginal = primaryMax;
        const secondaryMaxOriginal = secondaryMax;

        // Set min to 0 if no negative data
        if (!primaryHasNegative) {
            primaryMin = 0;
        }
        if (!secondaryHasNegative) {
            secondaryMin = 0;
        }

        // Ensure 0 is included in max if all values are negative
        if (primaryMax < 0) primaryMax = 0;
        if (secondaryMax < 0) secondaryMax = 0;

        // Align zero points only if BOTH axes have negative data
        if (primaryHasNegative && secondaryHasNegative) {
            const primaryRange = primaryMax - primaryMin;
            const secondaryRange = secondaryMax - secondaryMin;

            const primaryZeroRatio = Math.abs(primaryMin) / primaryRange;
            const secondaryZeroRatio = Math.abs(secondaryMin) / secondaryRange;

            // Skip alignment if either ratio is at or near 1 (all-negative data)
            // to avoid division by zero in the adjustment formula
            if (primaryZeroRatio < 0.99 && secondaryZeroRatio < 0.99) {
                // Use the larger ratio and adjust the other axis
                if (primaryZeroRatio > secondaryZeroRatio) {
                    const newSecondaryMin = -(secondaryMax * primaryZeroRatio / (1 - primaryZeroRatio));
                    secondaryMin = Math.min(secondaryMin, newSecondaryMin);
                } else if (secondaryZeroRatio > primaryZeroRatio) {
                    const newPrimaryMin = -(primaryMax * secondaryZeroRatio / (1 - secondaryZeroRatio));
                    primaryMin = Math.min(primaryMin, newPrimaryMin);
                }
            }
        }

        // Calculate nice step for a range
        const getNiceStep = (range: number): number => {
            if (range === 0) return 1;
            const roughStep = range / 5;
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const residual = roughStep / magnitude;

            if (residual <= 1.5) return magnitude;
            if (residual <= 3) return 2 * magnitude;
            if (residual <= 7) return 5 * magnitude;
            return 10 * magnitude;
        };

        // Calculate range from 0 (or min if negative) to max
        const primaryRange = primaryMaxOriginal - (primaryHasNegative ? primaryMin : 0);
        const secondaryRange = secondaryMaxOriginal - (secondaryHasNegative ? secondaryMin : 0);
        const primaryIsFlat = primaryRange === 0;
        const secondaryIsFlat = secondaryRange === 0;

        // Get nice steps for both axes
        // For flat ranges, use the magnitude of the value to avoid thousands of ticks
        const primaryStep = primaryIsFlat
            ? (primaryHasNegative ? Math.abs(primaryMin) || 1 : primaryMaxOriginal || 1)
            : getNiceStep(primaryRange);
        const secondaryStep = secondaryIsFlat
            ? (secondaryHasNegative ? Math.abs(secondaryMin) || 1 : secondaryMaxOriginal || 1)
            : getNiceStep(secondaryRange);

        // Calculate how many steps below and above zero for each axis
        // Use original max to avoid unnecessary extra ticks
        // For flat ranges, cap to 1 step to avoid tick explosion
        const primaryStepsBelow = primaryIsFlat
            ? (primaryHasNegative ? 1 : 0)
            : (primaryHasNegative ? Math.ceil(Math.abs(primaryMin) / primaryStep) : 0);
        const primaryStepsAbove = primaryIsFlat
            ? (primaryMaxOriginal > 0 ? 1 : 0)
            : (primaryMaxOriginal > 0 ? Math.ceil(primaryMaxOriginal / primaryStep) : 0);
        const secondaryStepsBelow = secondaryIsFlat
            ? (secondaryHasNegative ? 1 : 0)
            : (secondaryHasNegative ? Math.ceil(Math.abs(secondaryMin) / secondaryStep) : 0);
        const secondaryStepsAbove = secondaryIsFlat
            ? (secondaryMaxOriginal > 0 ? 1 : 0)
            : (secondaryMaxOriginal > 0 ? Math.ceil(secondaryMaxOriginal / secondaryStep) : 0);

        // Use the maximum steps below/above zero for both axes
        const stepsBelow = Math.max(primaryStepsBelow, secondaryStepsBelow);
        const stepsAbove = Math.max(primaryStepsAbove, secondaryStepsAbove);

        // Generate ticks with aligned zeros
        const generateAlignedTicks = (step: number): number[] => {
            const ticks: number[] = [];
            // Determine decimal places based on step size
            const decimalPlaces = step < 1 ? Math.ceil(-Math.log10(step)) : 0;
            const multiplier = Math.pow(10, decimalPlaces);

            for (let i = -stepsBelow; i <= stepsAbove; i++) {
                const tick = Math.round(i * step * multiplier) / multiplier;
                // Only add if not duplicate of last tick
                if (ticks.length === 0 || tick !== ticks[ticks.length - 1]) {
                    ticks.push(tick);
                }
            }
            return ticks;
        };

        const primaryTicks = generateAlignedTicks(primaryStep);
        const secondaryTicks = generateAlignedTicks(secondaryStep);

        // Domain from ticks
        const finalPrimaryMin = primaryTicks[0];
        const finalPrimaryMax = primaryTicks[primaryTicks.length - 1];
        const finalSecondaryMin = secondaryTicks[0];
        const finalSecondaryMax = secondaryTicks[secondaryTicks.length - 1];

        return {
            primary: [finalPrimaryMin, finalPrimaryMax],
            secondary: [finalSecondaryMin, finalSecondaryMax],
            primaryTicks,
            secondaryTicks,
        };
    };

    const { primary: primaryDomain, secondary: secondaryDomain, primaryTicks, secondaryTicks } = calculateAlignedDomains();

    const labelFormatter = (value: string | number, name: string) => {
        const rawValueType = GeneralGraphUtils.getMetricType(data, name as keyof TData, graphId);
        const formattedValue = formatKeyValue(value, rawValueType, get);

        const formattedName = t(name);

        return [formattedValue, formattedName];
    };

    return (
        <Card>
            <Highlights
                graphId={graphId}
                data={highlightsData}
                active={activeHighlights}
                isLoading={!!loading || !!error}
                handleAdd={(key) => handleAdd(key)}
            >
                {highlights.map(({ key, color, state, hidden }, index) => (
                    <Highlights.Item
                        key={GeneralGraphUtils.isString(key) ? key : index}
                        metricKey={String(key)}
                        color={color}
                        state={state}
                        hidden={hidden}
                        onHide={() => handleHide(key)}
                        onClick={() => handleClick(key)}
                    />
                ))}
            </Highlights>
            {canViewGraph && (
                <div className="w-full overflow-x-auto overflow-y-hidden">
                    <div className="min-w-[700px] h-96">
                        {loading || error || isEmpty ? (
                            <GraphStateRenderer error={!!error} loading={!!loading} empty={isEmpty} />
                        ) : (
                            <ResponsiveContainer>
                                <ComposedChart data={merge} margin={{ top: 20, right: 40, left: 20, bottom: 10 }}>
                                    <defs>
                                        {[...primaryKeys, ...secondaryKeys].map((key) => (
                                            <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={colorKeyMap.get(key)} stopOpacity={0.3} />
                                                <stop offset="100%" stopColor={colorKeyMap.get(key)} stopOpacity={0} />
                                            </linearGradient>
                                        ))}
                                    </defs>
                                    <XAxis
                                        axisLine={false}
                                        dataKey={X_AXIS_KEY}
                                        tickFormatter={(str) => dayjs(str).format(xAxisDateFormat)}
                                    />
                                    <CartesianGrid 
                                        vertical={false} 
                                        strokeDasharray="12" 
                                        opacity={0.4}
                                    />
                                    <YAxis
                                        yAxisId={"primary"}
                                        orientation={"left"}
                                        axisLine={false}
                                        domain={primaryDomain}
                                        ticks={primaryTicks}
                                        allowDataOverflow={false}
                                    />
                                    <YAxis
                                        yAxisId={"secondary"}
                                        orientation={"right"}
                                        axisLine={false}
                                        domain={secondaryDomain}
                                        ticks={secondaryTicks}
                                        allowDataOverflow={false}
                                    />
                                    <Tooltip
                                        formatter={labelFormatter}
                                        labelFormatter={(str) => dayjs(str).format(tooltipDateFormat)}
                                    />

                                    <Legend
                                        align="left"
                                        verticalAlign="bottom"
                                        layout="horizontal"
                                        wrapperStyle={{
                                            paddingLeft: 20,
                                            bottom: 0,
                                            cursor: "pointer",
                                        }}
                                        formatter={(value) => {
                                            const dataKey = String(value);
                                            const label = t(dataKey);
                                            const isHidden = hiddenLines.has(dataKey);
                                            return [
                                                <span
                                                    key={dataKey}
                                                    style={{
                                                        color: isHidden ? "#ccc" : "#000",
                                                        textDecoration: isHidden ? "line-through" : undefined,
                                                        marginRight: "12px",
                                                    }}
                                                >
                                                    {label}
                                                </span>,
                                            ];
                                        }}
                                        iconType="circle"
                                    />

                                    {primaryKeys.map((key) => {
                                        const isHidden = hiddenLines.has(key);
                                        const color = colorKeyMap.get(key);
                                        return (
                                            <Area
                                                yAxisId={"primary"}
                                                key={key}
                                                type={"monotone"}
                                                name={key}
                                                dataKey={key}
                                                strokeWidth={isHidden ? 0 : 2}
                                                stroke={color}
                                                fill={`url(#gradient-${key})`}
                                                fillOpacity={isHidden ? 0 : 1}
                                                activeDot={false}
                                                dot={false}
                                                hide={isHidden}
                                            />
                                        );
                                    })}

                                    {secondaryKeys.map((key) => {
                                        const isHidden = hiddenLines.has(key);
                                        const color = colorKeyMap.get(key);
                                        return (
                                            <Area
                                                yAxisId={"secondary"}
                                                key={key}
                                                type={"monotone"}
                                                name={key}
                                                dataKey={key}
                                                strokeWidth={isHidden ? 0 : 2}
                                                stroke={color}
                                                fill={`url(#gradient-${key})`}
                                                fillOpacity={isHidden ? 0 : 1}
                                                activeDot={false}
                                                dot={false}
                                                hide={isHidden}
                                            />
                                        );
                                    })}
                                </ComposedChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
};
